import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {GrowlService, UserService} from '@app/core';
import {
  ApprovalStatus,
  CommonDataService,
  CountryCodeAlpha2,
  DateService,
  DialogStyle40,
  EmergencyContact,
  EmergencyContactEmail,
  EmergencyContactPhone,
  EnumType,
  Format,
  PrimeNgSupportService,
  RegExpEmail,
  TableColumn,
  TableFormat,
  Validation
} from '@app/shared';
import {NgxIntlTelInputComponent} from "ngx-intl-tel-input";
import {classToClass} from "class-transformer";

/**
 * @author Jin Chun
 * @desc PrimeNG table component CRUD template. Instruction to create new component using this template
 * - create your component & service using cli
 *    - ng g component <component name>
 *    - cd <component name>
 *    - ng g service <component name>
 *    - ng g class <component name>
 * - all ts file change
 *    - copy and paste the content of the emergency-contact component, service, and class
 *    - change "EmergencyContact" to new component name with title cased
 *    - change "emergency-contact" to new component name
 * - component ts file change
 *    - change inputArray to appropriate variable name
 *    - change field names and headers in ngOnInit()
 */

@Component({
  selector: 'app-emergency-contact',
  templateUrl: './emergency-contact.component.html',
  styleUrls: ['./emergency-contact.component.css'],
  providers: [ConfirmationService]
})

export class EmergencyContactComponent implements OnInit,AfterViewInit {

  @ViewChild("phone") phone: NgxIntlTelInputComponent;
  @ViewChild('contactAction') contactActionTemplate: TemplateRef<any>;
  @Input() emergencyContacts: EmergencyContact[];
  @Output() emergencyContactChangeHandler = new EventEmitter<EmergencyContact[]>();

  DialogStyle460 = DialogStyle40;
  RegExpEmail = RegExpEmail;

  // standard format and validation
  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;
  ApprovalStatus: typeof ApprovalStatus = ApprovalStatus;

  readonly DefaultSortField = "type";

  selectedRow: EmergencyContact;                      // selected row from the datatable
  selectedRowIndex: number;                           // index of the selected row
  tableColumns: TableColumn[];                                // column headers and fields to display on the table

  // dialog objects
  emergencyContactToEdit: EmergencyContact;           // object used when add/edit selected row
  displayDialog: boolean;                             // when true, show the popup dialog to gather user input
  relationshipTypeDropDown: SelectItem[];
  contactTypeDropDown: SelectItem[];
  emailTypeDropDown: SelectItem[];
  phoneTypeDropDown: SelectItem[];
  visible: boolean = true;

  constructor(private datepipe: DatePipe, private dateService: DateService, private confirmationService: ConfirmationService, private user: UserService, private growl: GrowlService,
              private primeNgService: PrimeNgSupportService, private commonDataService: CommonDataService) {
  }

  /**
   * @desc initialize table columns, breacrumb and then get a list of emergency contacts from the backend
   */
  ngOnInit() {
    this.getEmergencyContactAndTypes();
  }

  ngAfterViewInit(): void {
    Promise.resolve(null).then(() => this.buildMenu());
  }



  /**
   * @desc retrieve valid relationship and contact types
   * @returns this.relationshipTypeDropDown - retreived valid relationship types as dropdown menu from the backend DB
   *          this.contactTypeDropDown - retreived valid contact types as dropdown menu from the backend DB
   */
  getEmergencyContactAndTypes(): void {

    this.commonDataService.getEnumPairAsDropdown(EnumType.Relationship).subscribe(relationshipTypes => {
      this.relationshipTypeDropDown = relationshipTypes;
    });

    this.commonDataService.getEnumPairAsDropdown(EnumType.Contact).subscribe(contactTypes => {
      this.contactTypeDropDown = contactTypes;
    });

    this.commonDataService.getEnumPairAsDropdown(EnumType.Email).subscribe(emailTypes => {
      this.emailTypeDropDown = emailTypes;
    });

    this.commonDataService.getEnumPairAsDropdown(EnumType.Phone).subscribe(phoneTypes => {
      this.phoneTypeDropDown = phoneTypes;
    });
  }

  /**
   * @desc show a pop-up dialog to gather new emergency contact information
   * @returns this.copiedRow - instantiated
   *          this.displayDialog - set to true, so popup dialog is shown to gather a new emergency contact info
   */
  onClickAddEmergencyContact(): void {
    this.emergencyContactToEdit = new EmergencyContact();
    this.emergencyContactToEdit.emails = new Array<EmergencyContactEmail>();
    this.emergencyContactToEdit.emails.push(new EmergencyContactEmail());
    this.emergencyContactToEdit.phones = new Array<EmergencyContactPhone>();
    this.emergencyContactToEdit.phones.push(new EmergencyContactPhone());
    this.displayDialog = true;
  }

  /**
   * @desc edit an existing emergency contact
   * @param emergencyContact - selected emergency contact to edit
   * @param rowIndex
   * @returns this.copiedRow - has copy of the selected emergency contact
   *          displayDialog - set it to true which in turn open up an edit dialog
   */
  onClickEditEmergencyContact(emergencyContact: EmergencyContact): void {
    const rowIndex = this.emergencyContacts.indexOf(emergencyContact);
    this.emergencyContactToEdit = classToClass(emergencyContact);
    this.selectedRowIndex = rowIndex;
    this.displayDialog = true;
  }

  /**
   * @desc save new emergency contact to the inputArray
   * @param emergencyContact - either new or selected emergency contact for edit
   * @returns: this.inputArray - new emergency contact added to the this array
   */
  onClickSaveEmergencyContact(emergencyContact: EmergencyContact): void {
    if (this.selectedRowIndex >= 0) {
      this.emergencyContacts = this.primeNgService.replaceByIndex(this.emergencyContacts, emergencyContact, this.selectedRowIndex);
      this.selectedRowIndex = -1;
    } else {
      this.emergencyContacts = this.primeNgService.unshift(this.emergencyContacts, emergencyContact);
    }
    this.triggerValueChangedEvent();
    this.displayDialog = false;
  }

  /**
   * @desc delete the selected row from the inputArray
   * @param emergencyContact - selected row to delete
   * @returns: this.inputArray - removed input row from the array
   */
  onClickDeleteEmergencyContact(emergencyContact: EmergencyContact): void {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this Emergency Contact?',
      header: 'Delete Emergency Contact',
      icon: 'fa fa-question-circle',
      accept: () => {
        this.emergencyContacts = this.primeNgService.deleteByObj(this.emergencyContacts, emergencyContact);
        this.triggerValueChangedEvent();
      },
      reject: () => {
      }
    });
  }

  triggerValueChangedEvent() {
    this.emergencyContactChangeHandler.emit(this.emergencyContacts);
  }

  private buildMenu(): void {
    this.tableColumns = [
      {field: 'type', header: 'Type', format: TableFormat.TitleCase},
      {field: 'firstName', header: 'First Name'},
      {field: 'lastName', header: 'Last Name'},
      {field: 'relationType', header: 'Relationship', format: TableFormat.TitleCase},
      {field: 'emailType', header: 'E-Mail Type', format: TableFormat.TitleCase},
      {field: 'emailAddr', header: 'E-Mail'},
      {field: 'phoneType', header: 'Phone Type', format: TableFormat.TitleCase},
      {field: 'phoneNumber', header: 'Phone #', format: TableFormat.Phone},
      {field: 'action', header: 'Action', template: this.contactActionTemplate},
    ];
  }
}
